import type { CallOutcome, Gender, MessageKind, Patient } from '@/lib/clinic/types'

import { yearsBetween, dateFromTimestamp, isISODate } from '@/lib/clinic/dates'

export const digitsOnly = (value: string): string => value.replace(/\D/g, '')

/** Accepts local 10-digit numbers and numbers with a country code. */
export const isValidPhone = (value: string): boolean => {
    const digits = digitsOnly(value)

    return digits.length >= 10 && digits.length <= 13
}

export const formatPhone = (value: string): string => {
    const digits = digitsOnly(value)

    if (digits.length === 10) return `${digits.slice(0, 5)} ${digits.slice(5)}`
    if (digits.length === 12 && digits.startsWith('91')) return `+91 ${digits.slice(2, 7)} ${digits.slice(7)}`

    return value.trim()
}

/** Digits WhatsApp expects in wa.me links: country code + number, no plus sign. */
export const toWhatsAppNumber = (phone: string, countryCode: string): string => {
    let digits = digitsOnly(phone)
    const code = digitsOnly(countryCode) || '91'

    if (digits.startsWith('00')) digits = digits.slice(2)
    if (digits.length === 11 && digits.startsWith('0')) digits = digits.slice(1)
    if (digits.length === 10) digits = `${code}${digits}`

    return digits
}

export const telHref = (phone: string): string => {
    const trimmed = phone.trim()
    const plus = trimmed.startsWith('+') ? '+' : ''

    return `tel:${plus}${digitsOnly(trimmed)}`
}

export const randomId = (prefix: string): string => {
    const random = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID().replace(/-/g, '').slice(0, 12)
        : Math.random().toString(36).slice(2, 14)

    return `${prefix}_${random}`
}

export const patientCode = (number: number): string => `DSC-${number}`

export const prescriptionCode = (number: number): string => `RX-${number}`

export const initials = (name: string): string => {
    const parts = name.trim().split(/\s+/).filter(part => /[a-z]/i.test(part))

    if (parts.length === 0) return '?'
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()

    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
}

export const GENDER_LABELS: Record<Gender, string> = {
    male: 'Male',
    female: 'Female',
    other: 'Other'
}

export const GENDER_SHORT: Record<Gender, string> = {
    male: 'M',
    female: 'F',
    other: 'O'
}

export const getPatientAge = (patient: Pick<Patient, 'age' | 'ageRecordedOn' | 'dateOfBirth' | 'createdAt'>, onDate: string): number | null => {
    if (isISODate(patient.dateOfBirth)) return yearsBetween(patient.dateOfBirth, onDate)
    if (patient.age === null || patient.age === undefined) return null

    const recordedOn = patient.ageRecordedOn ?? dateFromTimestamp(patient.createdAt)

    return patient.age + yearsBetween(recordedOn, onDate)
}

export const ageLabel = (age: number | null): string => {
    if (age === null) return '—'
    if (age === 1) return '1 yr'

    return `${age} yrs`
}

export const ageGenderLabel = (age: number | null, gender: Gender | null): string => {
    const parts = [age === null ? null : `${age}`, gender ? GENDER_SHORT[gender] : null].filter(Boolean)

    return parts.length ? parts.join(' / ') : '—'
}

export const CALL_OUTCOME_LABELS: Record<CallOutcome, string> = {
    'confirmed': 'Confirmed the date',
    'no-answer': 'No answer',
    'reschedule': 'Asked to reschedule',
    'call-back': 'Will call back'
}

export const MESSAGE_KIND_LABELS: Record<MessageKind, string> = {
    reminder: 'Returning reminder',
    overdue: 'Overdue follow-up',
    confirmation: 'Appointment confirmation',
    custom: 'Custom message'
}

export const pluralize = (count: number, singular: string, plural?: string): string => {
    return `${count} ${count === 1 ? singular : plural ?? `${singular}s`}`
}

export const splitLines = (value: string): string[] => {
    return value.split('\n').map(line => line.replace(/^[\s•\-*]+/, '').trim()).filter(Boolean)
}

export const normalizeText = (value: string): string => value.trim().replace(/\s+/g, ' ')
